import type { MaybeRefOrGetter } from "vue";
import { computed, onScopeDispose, readonly, ref, shallowRef, toValue, watch } from "vue";

import type { ListOption } from "../types";

/** A load that answers sooner never shows the skeleton. */
export const SKELETON_DELAY_MS = 150;

export interface ListOptionsLoaderOptions {
  /** Shown while nothing has loaded, and always without a loader. */
  options: MaybeRefOrGetter<ListOption[]>;
  loadOptions: MaybeRefOrGetter<(() => Promise<ListOption[]>) | undefined>;
}

export function useListOptionsLoader(options: ListOptionsLoaderOptions) {
  const loader = computed(() => toValue(options.loadOptions));
  const loaded = shallowRef<ListOption[] | null>(null);
  const running = ref(false);
  const failed = ref(false);
  const skeleton = ref(false);
  // Bumped by every call and every new loader: an answer that finds another value is stale.
  let request = 0;
  let skeletonTimer: ReturnType<typeof setTimeout> | undefined;

  const listOptions = computed(() => loaded.value ?? toValue(options.options));
  /** The options are the loader's answer, so "every option checked" means what it says. */
  const ready = computed(() => !loader.value || loaded.value !== null);
  /** A load runs and nothing has loaded yet for this loader; a reload behind a list is not busy. */
  const busy = computed(() => running.value && loaded.value === null);

  function stopSkeletonTimer() {
    clearTimeout(skeletonTimer);
    skeletonTimer = undefined;
  }

  watch(
    busy,
    (isBusy) => {
      stopSkeletonTimer();
      skeleton.value = false;
      if (isBusy) skeletonTimer = setTimeout(() => (skeleton.value = true), SKELETON_DELAY_MS);
    },
    { flush: "sync" },
  );

  watch(
    loader,
    () => {
      request++;
      loaded.value = null;
      running.value = false;
      failed.value = false;
    },
    { flush: "sync" },
  );

  onScopeDispose(() => {
    request++;
    stopSkeletonTimer();
  });

  /** Never rejects: a failed load keeps the last options. */
  async function load(): Promise<void> {
    const fn = loader.value;
    if (!fn) return;
    const current = ++request;
    failed.value = false;
    running.value = true;
    let result: ListOption[] | null = null;
    try {
      result = await new Promise<ListOption[]>((resolve) => resolve(fn()));
    } catch {
      // The app's loader reports its own errors; here the filter only keeps working.
    }
    if (current !== request) return;
    running.value = false;
    if (result) loaded.value = result;
    else failed.value = loaded.value === null;
  }

  return {
    options: listOptions,
    ready,
    busy,
    skeleton: readonly(skeleton),
    /** The last load rejected and nothing has loaded for this loader. */
    failed: readonly(failed),
    load,
  };
}
